import { createClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { StatusBar, Style } from "@capacitor/status-bar";
// Get environment variables from Vite
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseConfigError = getSupabaseConfigError();

function normalizeSupabaseProjectUrl(url) {
  if (!url) return url;
  let normalized = String(url).trim();
  normalized = normalized.replace(/\/+$/, "");
  normalized = normalized.replace(/\/(rest|auth|storage)\/v1$/i, "");
  normalized = normalized.replace(/\/+$/, "");
  return normalized;
}

// Validate URL format
function validateUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (e) {
    return false;
  }
}

function getSupabaseConfigError() {
  if (!supabaseUrl || !supabaseAnonKey) {
    return (
      "Missing Supabase environment variables. Please set:\n" +
      "VITE_SUPABASE_URL=https://<seu-projeto>.supabase.co\n" +
      "VITE_SUPABASE_ANON_KEY=<sua-anon-key>\n\n" +
      "Configure these variables in Vercel Environment Variables or in your local .env before building."
    );
  }

  const normalizedSupabaseUrl = normalizeSupabaseProjectUrl(supabaseUrl);

  if (!validateUrl(normalizedSupabaseUrl)) {
    return (
      "Invalid Supabase URL format. URL must start with http:// or https://\n" +
      "Current URL: " +
      supabaseUrl
    );
  }

  return null;
}

// Helper to create a validated client
function createValidatedClient() {
  if (supabaseConfigError) {
    return null;
  }

  const normalizedSupabaseUrl = normalizeSupabaseProjectUrl(supabaseUrl);

  // Create and return the client
  return createClient(normalizedSupabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
    },
  });
}

// Inicializa configurações específicas do App (Android/iOS)
export const initMobileApp = async () => {
  if (Capacitor.isNativePlatform()) {
    // Adiciona classe ao body para CSS específico
    document.body.classList.add("is-mobile-app");

    // Configura a barra de status (onde fica o relógio/bateria)
    try {
      await StatusBar.setStyle({ style: Style.Light });
      // Opcional: await StatusBar.setBackgroundColor({ color: '#f95738' }); // Cor do seu tema
    } catch (e) {
      console.warn("StatusBar não disponível");
    }

    // Gerencia o botão "Voltar" do Android
    App.addListener("backButton", ({ canGoBack }) => {
      if (!canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });
  }
};

// Helper para identificar se estamos no App (iOS/Android) ou Web
export const isApp = () =>
  Capacitor.getPlatform() !== "web" ||
  window.location.protocol === "capacitor:";

// Helper para Biometria (Requer: npm install capacitor-native-biometric)
export const performBiometricAuth = async (
  reason = "Autentique-se para entrar",
) => {
  if (!isApp()) return false;
  try {
    const { NativeBiometric } = await import("capacitor-native-biometric");
    const result = await NativeBiometric.isAvailable();
    if (!result.isAvailable) return false;

    await NativeBiometric.verifyIdentity({
      reason,
      title: "Log-in Biométrico",
      subtitle: "Use sua digital ou rosto",
      description: "Confirme sua identidade para acessar sua conta.",
    });
    return true;
  } catch (err) {
    console.error("Erro biometria:", err);
    return false;
  }
};

// Create a single instance of the Supabase client
const supabase = createValidatedClient();

// Expose client for Cypress E2E tests (window.Cypress only exists in Cypress browser)
if (typeof window !== "undefined" && window.Cypress && supabase) {
  window.__supabase = supabase;
}

// Helper: retorna true se o e-mail do usuário estiver confirmado
export const isUserEmailConfirmed = async () => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return !!user?.email_confirmed_at;
  } catch (err) {
    console.error("Erro ao verificar confirmação de e-mail:", err);
    return false;
  }
};

// Export the singleton instance
export { supabase };
export { supabaseConfigError };

// Default export for convenience
export default supabase;
